/**
 * Sidebar Navigation Configuration
 *
 * All navigation groups, items, and sub-menus defined here.
 * Sidebar.jsx reads from this config — NO inline nav data in the component.
 *
 * 9 Main Groups: Operations, Planung, Personal, Recruiting, Performance,
 *                Controlling, Kunden, Warehouse, Prozesse, System
 *
 * @since v2.222.0 (initial config extraction)
 * @updated v2.228.0 (restructured: 9→8 groups, merged Sales→Kunden, Flex→Personal,
 *          Prozesse→Warehouse, Admin+Dev→System)
 * @updated v2.272.0 (Prozesse→eigener Hauptmenüpunkt, 8→9 groups)
 */
import {
  IconHome, IconForecast, IconSchicht, IconPerformance,
  IconFeiertage, IconRohdaten, IconApi,
  IconAbwesenheiten, IconDeckungsbeitrag, IconReports,
  IconKostenstellen, IconOperations,
  IconMitarbeiter, IconKunden, IconStandorte, IconUsers, IconCases, IconCarrier,
  IconWarehouse, IconPortalDashboard, IconNews, IconDokumente, IconProfil,
  IconEinstellungen, IconZeiterfassung, IconUrlaub, IconKrankmeldung, IconVertrag, IconSegment,
  IconZeitvorgaben, IconIdleTime, IconPersonalplanung,
} from '../components/Icons';
import { Activity, Banknote, BookOpen, Bot, Brain, Briefcase, Building2, Calculator, Calendar as CalendarLc, CalendarCheck, CalendarClock, ClipboardList, CheckCircle2, CheckSquare, Clock, Code, Contact, CreditCard, Globe, GraduationCap, Handshake, Heart, History, Inbox, LayoutDashboard, LayoutGrid, LogOut, Mail, MapPin, MessageCircle, Mic, PackageSearch, Phone, QrCode, Receipt, Search, Settings, SlidersHorizontal, Timer, Truck, Upload, UserCheck, UserPlus, Users, BarChart3, GitBranch, ShieldAlert, ShieldCheck, Video } from 'lucide-react';

// ── Lucide icon wrapper (consistent 16×16) ──
const lc = (LucideIcon) => ({ style, ...p }) => <LucideIcon style={{ ...style, width: 16, height: 16 }} {...p} />;
const lc20 = (LucideIcon) => (p) => <LucideIcon width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...p} />;

// ══════════════════════════════════════════════════════════════
//  PORTAL SECTIONS (Mitarbeiter-Portal / Kunden-Portal)
// ══════════════════════════════════════════════════════════════

export const PORTAL_SECTIONS = [
  {
    labelKey: 'nav.portal',
    items: [
      { path: '/portal',              labelKey: 'nav.portal.dashboard',    Icon: IconPortalDashboard },
      { path: '/portal/zeiten',       labelKey: 'nav.portal.times',        Icon: IconZeiterfassung },
      { path: '/portal/abwesenheiten', labelKey: 'nav.portal.absences',   Icon: IconAbwesenheiten, hasBadge: 'portal_urlaub' },
      { path: '/portal/dokumente',    labelKey: 'nav.portal.documents',    Icon: IconDokumente },
      { path: '/portal/news',         labelKey: 'nav.portal.news',         Icon: IconNews, hasBadge: 'portal_news' },
      { path: '/portal/profil',       labelKey: 'nav.portal.myProfile',    Icon: IconProfil },
    ],
  },
];

// ══════════════════════════════════════════════════════════════
//  JV PARTNER SECTIONS (eingeschränkte Sidebar)
// ══════════════════════════════════════════════════════════════

export const JV_PARTNER_SECTIONS = [
  {
    labelKey: 'nav.controlling',
    items: [
      { path: '/finecom', labelKey: 'sidebar.partnerMarge', Icon: lc(Receipt), modul: 'finecom_partner' },
    ],
  },
];

// ══════════════════════════════════════════════════════════════
//  FOCUS MODES — UX filter for sidebar (NOT a permission system)
// ══════════════════════════════════════════════════════════════

export const FOCUS_MODES = [
  { key: 'alle',     labelKey: 'sidebar.focus.alle' },
  { key: 'ops',      labelKey: 'sidebar.focus.ops' },
  { key: 'hr',       labelKey: 'sidebar.focus.hr' },
  { key: 'finanzen', labelKey: 'sidebar.focus.finanzen' },
  { key: 'kunden',   labelKey: 'sidebar.focus.kunden' },
];

// Shorthand for all-mode items (visible everywhere)
const ALL = ['alle', 'ops', 'hr', 'finanzen', 'kunden'];

// ══════════════════════════════════════════════════════════════
//  MAIN SIDEBAR GROUPS (8 Groups)
// ══════════════════════════════════════════════════════════════

export const SIDEBAR_GROUPS = [
  // ─── 0. FLOWAI (prominent oben, #1085) ───
  {
    labelKey: 'nav.group.flowai',
    items: [
      { path: '/flowai',            labelKey: 'nav.flowai.cockpit',    Icon: lc(Brain),            modul: 'flowai', modes: ALL },
      { path: '/flowai/uebersicht', labelKey: 'nav.flowai.uebersicht', Icon: lc(LayoutDashboard),  modul: 'flowai', modes: ALL },
      { path: '/flowai/chat',       labelKey: 'nav.flowai.chat',       Icon: lc(MessageCircle),    modul: 'flowai', modes: ALL },
      { path: '/flowai/voice',      labelKey: 'nav.flowai.voice',      Icon: lc(Mic),           modul: 'flowai', modes: ALL },
      { path: '/prozesse/telegram', labelKey: 'nav.prozesse_telegram',  Icon: lc(Bot),           modul: 'flowai', modes: ALL },
      { path: '/flowai/approvals',  labelKey: 'nav.flowai.approvals',  Icon: lc(Inbox),         modul: 'flowai', modes: ALL },
      { path: '/flowai/workspaces', labelKey: 'nav.flowai.workspaces', Icon: lc(LayoutGrid),    modul: 'flowai', modes: ALL },
      { path: '/flowai/playbooks',  labelKey: 'nav.flowai.playbooks',  Icon: lc(BookOpen),      modul: 'flowai', modes: ALL },
      { path: '/flowai/skills',     labelKey: 'nav.flowai.skills',     Icon: lc(Code),          modul: 'flowai', modes: ALL },
      { path: '/flowai/events',     labelKey: 'nav.flowai.events',     Icon: lc(Activity),      modul: 'flowai', modes: ALL },
      { path: '/flowai/memory',     labelKey: 'nav.flowai.memory',     Icon: lc(ClipboardList), modul: 'flowai', modes: ALL },
      { path: '/flowai/audit',      labelKey: 'nav.flowai.audit',      Icon: lc(ShieldCheck),   modul: 'flowai', modes: ALL },
    ],
  },

  // ─── 1. OPERATIONS ───
  {
    labelKey: 'nav.group.operations',
    items: [
      { path: '/operations', labelKey: 'nav.operations', Icon: IconOperations, modul: 'operations', modes: ['alle', 'ops'] },
      { path: '/operations/lieferauftraege-pruefung', labelKey: 'nav.outboundInvestigations', Icon: lc(PackageSearch), modul: 'operations', modes: ['alle', 'ops'] },
      { path: '/forecast', labelKey: 'nav.tagesplanung', Icon: lc(CalendarCheck), modul: 'forecast', modes: ['alle', 'ops'] },
      { path: '/forecast-overview', labelKey: 'nav.forecast', Icon: IconForecast, modul: 'forecast', modes: ['alle', 'ops'] },
      { path: '/packplatz-timer', labelKey: 'nav.packplatzTimer', Icon: lc(Timer), modul: 'pack_timer', modes: ['alle', 'ops'] },
      { path: '/aufnahmen', labelKey: 'nav.aufnahmen', Icon: lc(Video), modul: 'admin', modes: ['alle', 'ops'] },
    ],
  },

  // ─── 2. PLANUNG ───
  {
    labelKey: 'nav.group.planning',
    items: [
      { path: '/schichtplanung/wochenplan', labelKey: 'nav.wochenplan', Icon: lc(CalendarClock), modul: 'schichtplanung', modes: ['alle', 'ops'] },
      { path: '/schichtplanung', labelKey: 'nav.shift_planning', Icon: IconSchicht, modul: 'schichtplanung', modes: ['alle', 'ops'] },
      { path: '/personalplanung', labelKey: 'nav.personalplanung', Icon: IconPersonalplanung, modul: 'schichtplanung', modes: ['alle', 'ops', 'hr'] },
    ],
  },

  // ─── 3. PERSONAL (HR) ───
  {
    labelKey: 'nav.group.personal',
    items: [
      { path: '/mitarbeiter', labelKey: 'nav.employees', Icon: IconMitarbeiter, modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/vertraege', labelKey: 'nav.dienstvertraege', Icon: lc20(ClipboardList), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/anwesenheiten', labelKey: 'nav.attendance', Icon: IconAbwesenheiten, modul: 'mitarbeiter,anwesenheiten_readonly', modes: ['alle', 'hr'] },
      { path: '/anwesenheiten/ausnahmen', labelKey: 'nav.ausnahmen', Icon: lc20(ShieldAlert), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/abwesenheiten/kalender', labelKey: 'nav.abwesenheiten', Icon: IconUrlaub, modul: 'urlaub', modes: ['alle', 'hr'] },
      { path: '/zeitkonto', labelKey: 'nav.zeitkonto', Icon: lc20(Clock), modul: 'zeitkonto', modes: ['alle', 'hr'] },
      { path: '/lohnverrechnung', labelKey: 'nav.lohnverrechnung', Icon: lc(Banknote), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/mitarbeiter/pulse', labelKey: 'nav.maPulse', Icon: lc20(Activity), modul: 'performance', modes: ['alle', 'hr'] },
      { path: '/ausweise', labelKey: 'nav.ausweise', Icon: lc20(CreditCard), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/organigramm', labelKey: 'nav.organigramm', Icon: lc20(Building2), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/hr-audit', labelKey: 'nav.hrAudit', Icon: lc20(ShieldCheck), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/hr/austritte', labelKey: 'nav.hrAustritte', Icon: lc20(LogOut), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
    ],
  },

  // ─── 3b. RECRUITING (unified Fest + Flex) ───
  {
    labelKey: 'nav.group.recruiting',
    items: [
      { path: '/recruiting', labelKey: 'nav.recruiting_dashboard', Icon: lc20(LayoutDashboard), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/recruiting/bewerber', labelKey: 'nav.recruiting_bewerber', Icon: lc20(Users), modul: 'mitarbeiter', hasBadge: 'recruiting_ungelesen', modes: ['alle', 'hr'] },
      { path: '/recruiting/stellen', labelKey: 'nav.recruiting_stellen', Icon: lc20(Briefcase), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/recruiting/einsaetze', labelKey: 'nav.recruiting_einsaetze', Icon: lc20(CalendarCheck), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/recruiting/pool', labelKey: 'nav.recruiting_pool', Icon: lc20(UserCheck), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/recruiting/reports', labelKey: 'nav.recruiting_reports', Icon: lc20(BarChart3), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
      { path: '/recruiting/einstellungen', labelKey: 'nav.recruiting_einstellungen', Icon: lc20(Settings), modul: 'mitarbeiter', modes: ['alle', 'hr'] },
    ],
  },

  // ─── 4. PERFORMANCE ───
  {
    labelKey: 'nav.group.performance',
    items: [
      { path: '/performance', labelKey: 'nav.performance', Icon: IconPerformance, modul: 'performance', modes: ['alle', 'ops'] },
      { path: '/idle-time', labelKey: 'nav.idle_time', Icon: IconIdleTime, modul: 'reports', modes: ['alle', 'ops'] },
      { path: '/zeitvorgaben-analyse', labelKey: 'nav.time_targets', Icon: IconZeitvorgaben, modul: 'deckungsbeitrag', modes: ['alle', 'ops'] },
    ],
  },

  // ─── 5. CONTROLLING ───
  {
    labelKey: 'nav.group.controlling',
    items: [
      { path: '/controlling/kpi', labelKey: 'nav.controllingKpi', Icon: lc(BarChart3), modul: 'deckungsbeitrag', modes: ['alle', 'finanzen'] },
      { path: '/controlling/zeit-kosten', labelKey: 'nav.controllingZeitKosten', Icon: lc(Clock), modul: 'controlling', modes: ['alle', 'finanzen'] },
      { path: '/deckungsbeitrag', labelKey: 'nav.contribution_margin', Icon: IconDeckungsbeitrag, modul: 'deckungsbeitrag', modes: ['alle', 'finanzen'] },
      { path: '/jv-deckungsbeitrag', labelKey: 'nav.jvDeckungsbeitrag', Icon: lc(Handshake), modul: 'deckungsbeitrag', modes: ['alle', 'finanzen'] },
      { path: '/personalkosten', labelKey: 'nav.personalkosten', Icon: lc(Banknote), modul: 'deckungsbeitrag', modes: ['alle', 'finanzen', 'hr'] },
      { path: '/kostenstellen', labelKey: 'nav.segment_analysis', Icon: IconKostenstellen, modul: 'deckungsbeitrag', modes: ['alle', 'finanzen'] },
      { path: '/carrier-kontrolle', labelKey: 'nav.carrier_control', Icon: IconCarrier, modul: 'carrier_kontrolle', modes: ['alle', 'finanzen'] },
      { path: '/finecom', labelKey: 'nav.finecom', Icon: lc(Receipt), modul: 'deckungsbeitrag', modes: ['alle', 'finanzen'] },
      { path: '/reports', labelKey: 'nav.reports', Icon: IconReports, modul: 'reports', modes: ['alle', 'finanzen'] },
    ],
  },

  // ─── 6. KUNDEN (+ Sales + Preiskalkulator) ───
  {
    labelKey: 'nav.group.customers',
    items: [
      { path: '/kunden', labelKey: 'nav.customer_management', Icon: IconKunden, modul: 'kunden', modes: ['alle', 'kunden'] },
      { path: '/firmen-registrierungen', labelKey: 'nav.companyRegistrations', Icon: lc(Building2), modul: 'kunden', modes: ['alle', 'kunden'] },
      { path: '/kontakte', labelKey: 'nav.contacts', Icon: lc(Contact), modul: 'kunden', modes: ['alle', 'kunden'] },
      { path: '/commitments', labelKey: 'nav.commitments', Icon: lc(Handshake), modul: 'commitments', hasBadge: 'commitments_pending', modes: ['alle', 'kunden'] },
      { path: '/zufriedenheit', labelKey: 'nav.zufriedenheit', Icon: lc(Heart), modul: 'cases', modes: ['alle', 'kunden'] },
      { path: '/kunden/adressklaerungen', labelKey: 'adressklaerung.title', Icon: lc(MapPin), modul: 'cases', modes: ['alle', 'kunden'] },
      {
        path: '/cases/liste', labelKey: 'nav.cases', Icon: IconCases, modul: 'cases', hasBadge: 'cases', modes: ['alle', 'kunden'],
        subItems: [
          { path: '/cases/reports', labelKey: 'nav.cases.reports' },
          { path: '/cases/flowai-report', labelKey: 'nav.cases.flowaiReport' },
          { path: '/cases/konfiguration', labelKey: 'nav.cases.configuration' },
        ],
      },
      {
        path: '/claims', labelKey: 'claims.title', Icon: lc(ShieldAlert), modul: 'claims', modes: ['alle', 'kunden'],
        subItems: [
          { path: '/claims/inbox', labelKey: 'claims.unassigned.title' },
          { path: '/claims/knowledge', labelKey: 'claims.knowledge.title' },
          { path: '/claims/settings', labelKey: 'claims.settings.title', requireFull: true },
        ],
      },
      {
        labelKey: 'nav.sales', Icon: lc(BarChart3), modul: 'sales', hasBadge: 'sales_unread', modes: ['alle', 'kunden'],
        subItems: [
          { path: '/sales/dashboard', labelKey: 'nav.salesDashboard' },
          { path: '/sales/funnels', labelKey: 'nav.salesFunnels' },
          { path: '/sales/anfragen', labelKey: 'nav.salesAnfragen', hasBadge: 'sales_unread' },
          { path: '/sales/onboarding', labelKey: 'nav.salesOnboarding' },
          { path: '/sales/workflows', labelKey: 'nav.salesWorkflows' },
          { path: '/sales/lead-finder', labelKey: 'nav.salesLeadFinder' },
          { path: '/sales/einstellungen', labelKey: 'nav.salesEinstellungen' },
        ],
      },
      {
        labelKey: 'nav.calculator', Icon: lc(Calculator), modul: 'sales', modes: ['alle', 'kunden'],
        subItems: [
          { path: '/preiskalkulator', labelKey: 'nav.calculator' },
          { path: '/preiskalkulator/config', labelKey: 'nav.calcConfig' },
          { path: '/preiskalkulator/routen', labelKey: 'nav.calcRoutes' },
          { path: '/preiskalkulator/anfragen', labelKey: 'nav.calcLeads' },
        ],
      },
    ],
  },

  // ─── 7. WAREHOUSE ───
  {
    labelKey: 'nav.group.warehouse',
    items: [
      { path: '/inbound-management', labelKey: 'nav.inboundManagement', Icon: lc(Truck), modul: 'operations', modes: ['alle', 'ops'], beta: true },
      { path: '/warehouse/lagerplaetze', labelKey: 'nav.warehouseLagerplaetze', Icon: IconWarehouse, modul: 'operations', modes: ['alle', 'ops'] },
      { path: '/warehouse/simulation', labelKey: 'nav.warehouseSimulation', Icon: lc(Building2), modul: 'operations', modes: ['alle', 'ops'] },
      { path: '/warehouse/einrichten', labelKey: 'nav.warehouseEinrichten', Icon: lc(SlidersHorizontal), modul: 'operations', modes: ['alle', 'ops'] },
    ],
  },

  // ─── 8. PROZESSE — #1725 P1-Polish: normale graue Gruppen-Überschrift wie RECRUITING
  //     (kein blauer Collapse-Parent mehr — #1724-FIX1 behielt die falsche Hälfte).
  //     Items flach: Abteilungen (Hybrid-Landing), Vorschläge, QM-Pipeline. ───
  {
    labelKey: 'nav.group.processes',
    items: [
      { path: '/prozesse/abteilungen', labelKey: 'nav.prozesse_abteilungen', Icon: lc(LayoutGrid), modul: 'admin', modes: ['alle', 'ops'] },
      { path: '/qm/feedback', labelKey: 'nav.qm_feedback', Icon: lc(MessageCircle), modul: 'admin', modes: ['alle', 'ops'] },
      { path: '/qm/pipeline', labelKey: 'nav.qm_pipeline', Icon: lc(GitBranch), modul: 'admin', modes: ['alle', 'ops'] },
    ],
  },

  // ─── 9. TELEFONIE (#1430 — UI-Dach, Unterseiten = Folgetickets) ───
  {
    labelKey: 'nav.group.telefonie',
    items: [
      { path: '/telefonie', labelKey: 'nav.telefonie.dashboard', Icon: lc(Phone), modul: 'telefonie', modes: ['alle', 'ops'] },
      { path: '/telefonie/verlauf', labelKey: 'nav.telefonie.verlauf', Icon: lc(History), modul: 'telefonie', modes: ['alle', 'ops'] },
      { path: '/telefonie/aufnahmen', labelKey: 'nav.telefonie.aufnahmen', Icon: lc(Mic), modul: 'telefonie', modes: ['alle', 'ops'] },
      { path: '/telefonie/regeln', labelKey: 'nav.telefonie.regeln', Icon: lc(GitBranch), modul: 'telefonie', modes: ['alle', 'ops'] },
      { path: '/telefonie/einstellungen', labelKey: 'nav.telefonie.einstellungen', Icon: lc(Settings), modul: 'telefonie', modes: ['alle', 'ops'] },
    ],
  },

];

// ── System items (moved to AdminHubPage, still used by CommandPalette search) ──
export const SYSTEM_ITEMS = [
  { path: '/admin/users', labelKey: 'nav.user_management', modul: 'benutzerverwaltung' },
  { path: '/admin/segmente', labelKey: 'nav.customer_segments', modul: 'admin' },
  { path: '/admin/email', labelKey: 'nav.email_management', modul: 'admin' },
  { path: '/admin/chat', labelKey: 'nav.chat_admin', modul: 'admin' },
  { path: '/admin/sms-templates', labelKey: 'nav.sms_templates', modul: 'admin' },
  { path: '/einstellungen', labelKey: 'nav.settings', modul: 'einstellungen' },
  { path: '/feiertage', labelKey: 'nav.public_holidays', modul: 'admin' },
  { path: '/standorte', labelKey: 'nav.locations', modul: 'admin' },
  { path: '/rohdaten', labelKey: 'nav.raw_data', modul: 'admin' },
  { path: '/data-hub', labelKey: 'nav.data_hub', modul: 'admin' },
  { path: '/admin/deployment', labelKey: 'nav.deployment', modul: 'deployment', devOnly: true },
  { path: '/entwicklung/system-pulse', labelKey: 'nav.system_pulse', modul: 'admin' },
  { path: '/dev-handbuch', labelKey: 'nav.dev_handbook', modul: 'admin' },
  { path: '/handbuch', labelKey: 'nav.manual', modul: 'admin' },
  { path: '/changelog', labelKey: 'nav.changelog', modul: 'admin' },
];

// ── Forecast sub-items (moved to SharedForecastTabs, still used by CommandPalette search) ──
export const FORECAST_SUB_ITEMS = [
  { path: '/forecast-overview', labelKey: 'nav.forecastCockpit' },
  { path: '/forecast-review', labelKey: 'nav.forecastReview' },
  { path: '/forecast', labelKey: 'nav.tagesplanung' },
  { path: '/forecast/bulk', labelKey: 'nav.forecastBulk' },
  { path: '/forecast/quality', labelKey: 'nav.forecastQuality' },
  { path: '/forecast-review?seg=aktionszeitraeume', labelKey: 'nav.forecastPromo' },
];

// ── Lohnverrechnung sub-items (moved to SharedLohnverrechnungTabs, still used by CommandPalette search) ──
export const LOHNVERRECHNUNG_SUB_ITEMS = [
  { path: '/lohnverrechnung', labelKey: 'nav.lohnverrechnung.archiv' },
  { path: '/lohnverrechnung/import', labelKey: 'nav.lohnverrechnung.import' },
  { path: '/lohnverrechnung/einstellungen', labelKey: 'nav.lohnverrechnung.einstellungen' },
];

// ── Abwesenheiten sub-items (moved to SharedAbwesenheitenTabs, still used by CommandPalette search) ──
export const ABWESENHEITEN_SUB_ITEMS = [
  { path: '/abwesenheiten/kalender', labelKey: 'nav.abwesenheitenKalender' },
  { path: '/abwesenheiten/antraege', labelKey: 'nav.abwesenheitenAntraege' },
  { path: '/abwesenheiten/uebersicht', labelKey: 'nav.abwesenheitenUebersicht' },
];

// ── Home + Dashboard + Tasks (top items — gated by 'basis' module) ──
export const HOME_ITEM = { path: '/', labelKey: 'nav.home', Icon: IconHome, modul: 'basis', mode: 'admin' };
export const DASHBOARD_ITEM = { path: '/dashboard', labelKey: 'nav.dashboard', Icon: lc(LayoutDashboard), modul: 'basis', mode: 'admin' };
export const TASKS_ITEM = { path: '/aufgaben', labelKey: 'nav.tasks', Icon: lc(CheckSquare), modul: 'basis', mode: 'admin' };
export const MEINE_AUFGABEN_ITEM = { path: '/meine-aufgaben', labelKey: 'nav.meineAufgabenPersoenlich', Icon: lc(CheckCircle2), modul: 'basis', mode: 'admin' };
export const ZEITERFASSUNG_ITEM = { path: '/zeiterfassung', labelKey: 'nav.zeiterfassung', Icon: IconZeiterfassung, modul: 'zeiterfassung', mode: 'admin' };
export const KALENDER_ITEM = { path: '/kalender', labelKey: 'nav.kalender', Icon: lc(CalendarLc), modul: 'basis', mode: 'admin' };
export const ADMIN_ITEM = { path: '/admin', labelKey: 'adminHub.title', Icon: lc(Settings), modul: 'admin' };
